import React, { useEffect } from "react";
import { Container, Col, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import { get } from "lodash";
import styles from "../styles.scss";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import OtherSettings, { CharitySettingsValues } from "../common/otherSettings";
import { DEFAULT_MAX_VIDEO_LENGTH_SECONDS } from "../../../../../../utils/onboarding/dashboardSequencing";

interface Props extends ServiceCallerProps {
  charityId: string;
  configLoaded: boolean;
  otherSettings: CharitySettingsValues;
}

const CharitySettingsPage: React.FC<Props> = ({
  charityId,
  configLoaded,
  otherSettings,
  isSuperadmin,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSuperadmin) {
      navigate("/contacts", { replace: true });
    }
  }, [isSuperadmin, navigate]);

  if (!isSuperadmin) {
    return null;
  }

  return (
    <Container className={styles.root}>
      <Row className={styles.mainContainer}>
        <Col xs={12} className={styles.cardBackground}>
          <OtherSettings
            charityId={charityId}
            configLoaded={configLoaded}
            settings={otherSettings}
          />
        </Col>
      </Row>
    </Container>
  );
};

const mapCharitySettings = (
  firebase: any,
  charityId: string,
  configLoaded: boolean
): CharitySettingsValues => {
  const configPath = `data.${charityId}.config`;
  const integrationsPath = `data.${charityId}.internal.integrations`;
  const rawMaxVideoLength = configLoaded
    ? get(firebase, `${configPath}.maxVideoLength`)
    : undefined;
  const maxVideoLength =
    typeof rawMaxVideoLength === "number" && rawMaxVideoLength >= 1
      ? rawMaxVideoLength
      : DEFAULT_MAX_VIDEO_LENGTH_SECONDS;

  return {
    enableVirtuous: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableVirtuous`, false))
      : false,
    enableDonorPerfect: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableDonorPerfect`, false))
      : false,
    enablePostGiveButtonLabel: configLoaded
      ? Boolean(get(firebase, `${configPath}.enablePostGiveButtonLabel`, false))
      : false,
    enablePostGiveButtonUrl: configLoaded
      ? Boolean(get(firebase, `${configPath}.enablePostGiveButtonUrl`, false))
      : false,
    enableMotivationCode: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableMotivationCode`, false))
      : false,
    enableEmbeddedPost: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableEmbeddedPost`, false))
      : false,
    enableInformativePost: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableInformativePost`, false))
      : false,
    enableTemporarySegments: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableTemporarySegments`, false))
      : false,
    enableTimeRestriction: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableTimeRestriction`, false))
      : false,
    enableGifPreview: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableGifPreview`, false))
      : false,
    emailPostNotifications: configLoaded
      ? Boolean(get(firebase, `${configPath}.emailPostNotifications`, false))
      : false,
    emailPostNotificationsDefault: configLoaded
      ? Boolean(
          get(firebase, `${configPath}.emailPostNotificationsDefault`, false)
        )
      : false,
    smsPostNotifications: configLoaded
      ? Boolean(get(firebase, `${configPath}.smsPostNotifications`, false))
      : false,
    smsPostNotificationsDefault: configLoaded
      ? Boolean(
          get(firebase, `${configPath}.smsPostNotificationsDefault`, false)
        )
      : false,
    enableOnlySmsPost: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableOnlySmsPost`, false))
      : false,
    defaultMessageType:
      configLoaded &&
      get(firebase, `${configPath}.defaultMessageType`, "onlyMms") === "onlySms"
        ? "onlySms"
        : "onlyMms",
    enableThankYouFeature: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableThankYouFeature`, false))
      : false,
    disableThankYouDonationEmail: configLoaded
      ? Boolean(
          get(firebase, `${configPath}.disableThankYouDonationEmail`, false)
        )
      : false,
    enableBillingFailureAlerts: configLoaded
      ? get(firebase, `${configPath}.enableBillingFailureAlerts`, true) !== false
      : true,
    emailLookupEnabled: configLoaded
      ? get(firebase, `${configPath}.emailLookupEnabled`, true) !== false
      : true,
    enableAdminMetrics: configLoaded
      ? Boolean(get(firebase, `${configPath}.enableAdminMetrics`, false))
      : false,
    adminPushNotifications: configLoaded
      ? get(firebase, `${configPath}.adminPushNotifications`, true) !== false
      : true,
    isVirtuousConnected: configLoaded
      ? Boolean(get(firebase, `${integrationsPath}.virtuousConfig.connected`, false))
      : false,
    isDonorPerfectConnected: configLoaded
      ? Boolean(
          get(firebase, `${integrationsPath}.donorPerfectConfig.connected`, false)
        )
      : false,
    donorPerfectEnableInboundGiftNotifications: configLoaded
      ? get(
          firebase,
          `${integrationsPath}.donorPerfectConfig.enableInboundGiftNotifications`,
          true
        ) !== false
      : true,
    maxVideoLength,
  };
};

const mapStateToProps = (state: any) => {
  const { user, firebase } = state;
  const charityId = user.currentCharity?.id || "";

  const configLoaded = Boolean(
    charityId &&
      isLoaded(firebase.data[charityId]) &&
      isLoaded(get(firebase, `data.${charityId}.config`))
  );

  return {
    charityId,
    configLoaded,
    otherSettings: mapCharitySettings(firebase, charityId, configLoaded),
  };
};

export default connect(mapStateToProps)(
  firebaseConnect((_props: any, store: any) => {
    const charityId = store.getState().user.currentCharity?.id;
    return charityId
      ? [`${charityId}/config`, `${charityId}/internal/integrations`]
      : [];
  })(serviceCaller(withStyles(styles)(CharitySettingsPage)))
);
